import { afterEveryRender, afterNextRender, inject, Injector, Service } from '@angular/core'
import { Location } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { Params, UrlTree } from '@angular/router'
import { NavigationEnd, PRIMARY_OUTLET, Router, UrlSegment, UrlSegmentGroup } from '@angular/router'
import { filter } from 'rxjs'
import { PopupService } from '../popup-service/popup.service'

/** Routes a page above the current screen without replacing its component. */
@Service()
export class PageNavigationService {
  private readonly router = inject(Router)
  private readonly overlays = inject(PopupService)
  private readonly location = inject(Location)
  private readonly injector = inject(Injector)
  private redirectTarget?: { navigationId: number; outlet: string }

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(event => {
        const target = this.redirectTarget?.navigationId === event.id ? this.redirectTarget.outlet : undefined
        const state = this.location.getState()
        const order =
          state && typeof state === 'object' && 'mobileOverlayOrder' in state && Array.isArray(state.mobileOverlayOrder)
            ? state.mobileOverlayOrder.filter((outlet): outlet is string => typeof outlet === 'string')
            : []
        const activate = (): void => {
          for (const outlet of order) {
            this.overlays.activate(outlet)
          }

          if (target) {
            this.overlays.activate(target)
          }
        }

        activate()
        // New outlet components register after navigation. Apply the order once they have rendered.
        afterNextRender(
          () => {
            if (this.router.lastSuccessfulNavigation()?.id === event.id) {
              activate()
            }
          },
          { injector: this.injector }
        )
      })
    afterEveryRender(() => {
      if (this.router.currentNavigation()) {
        return
      }

      const state = this.location.getState()

      if (state && typeof state === 'object') {
        const order = this.overlays.outletOrder()

        if (!('mobileOverlayOrder' in state) || JSON.stringify(state.mobileOverlayOrder) !== JSON.stringify(order)) {
          this.location.replaceState(this.router.url, '', { ...state, mobileOverlayOrder: order })
        }
      }
    })
  }

  /**
   * Builds a page URL for normal entry and direct links.
   * @param {readonly string[]} segments Route segments for the page outlet.
   * @param {string} fallbackUrl Background screen when there is no current app screen.
   * @param {Params} queryParams Parameters supplied by the incoming link.
   * @param {string} outlet Outlet owned by this page.
   * @param {readonly string[]} queryKeys Parameters to replace when opening this page.
   * @returns {UrlTree} The retained screen with a page above it.
   */
  pageUrl(
    segments: readonly string[],
    fallbackUrl: string,
    queryParams: Params = {},
    outlet = 'page',
    queryKeys: readonly string[] = []
  ): UrlTree {
    const current = this.router.parseUrl(this.router.url)
    let route = this.router.routerState.snapshot.root
    let isScreen = false

    while (route) {
      isScreen ||= route.data['retainedScreen'] === true

      const primary = route.children.find(child => child.outlet === PRIMARY_OUTLET)

      if (!primary) {
        break
      }

      route = primary
    }

    const url = isScreen ? current : this.router.parseUrl(fallbackUrl)

    url.root.children[outlet] = new UrlSegmentGroup(
      segments.map(segment => new UrlSegment(segment, {})),
      {}
    )

    for (const key of queryKeys) {
      delete url.queryParams[key]
    }

    url.queryParams = { ...url.queryParams, ...queryParams }

    return url
  }

  /**
   * Records the outlet requested by a legacy redirect without activating a cancelled navigation.
   * @param {UrlTree} url Redirect destination.
   * @param {string} [outlet=page] Outlet to show after the redirect completes.
   * @returns {UrlTree} The redirect destination.
   */
  redirect(url: UrlTree, outlet = 'page'): UrlTree {
    const navigation = this.router.currentNavigation()

    if (navigation) {
      this.redirectTarget = { navigationId: navigation.id, outlet }
    }

    return url
  }

  /**
   * Opens a routed overlay, bringing an existing instance above other surfaces.
   * @param {UrlTree} url URL of the page and its retained background.
   * @param {string} outlet Named outlet to activate after navigation.
   * @returns {Promise<boolean>} Whether the router completed a new navigation.
   */
  async open(url: UrlTree, outlet = 'page'): Promise<boolean> {
    const order = this.overlays.outletOrder().filter(current => current !== outlet)
    const navigated = await this.router.navigateByUrl(url, { state: { mobileOverlayOrder: [...order, outlet] } })

    // Reopening the same URL skips navigation but still needs to activate its surface.
    if (navigated || this.router.url === this.router.serializeUrl(url)) {
      this.overlays.activate(outlet)
    }

    return navigated
  }

  /**
   * Removes a page while preserving the background and other overlays.
   * @param {string} outlet Outlet owned by the closing page.
   * @param {readonly string[]} queryKeys Parameters owned by that page.
   */
  close(outlet = 'page', queryKeys: readonly string[] = []): void {
    const url = this.router.parseUrl(this.router.url)

    delete url.root.children[outlet]

    for (const key of queryKeys) {
      delete url.queryParams[key]
    }

    void this.router.navigateByUrl(url, { replaceUrl: true })
  }
}
